<template>
    <div class="perm">
        <!-- context: what is being secured -->
        <div v-if="contextName" class="perm__context">
            <span class="perm__context-icon">
                <v-icon small>{{ contextIcon }}</v-icon>
            </span>
            <div class="perm__context-text">
                <div class="perm__context-eyebrow">{{ tr('AccessControl', 'Access control', 'التحكم بالصلاحيات') }}</div>
                <div class="perm__context-name">{{ contextName }}</div>
            </div>
        </div>

        <!-- ── grant access ─────────────────────────────────────────── -->
        <section class="perm-card">
            <header class="perm-card__head">
                <span class="perm-card__head-icon">
                    <v-icon small>mdi-account-key-outline</v-icon>
                </span>
                <div class="perm-card__head-text">
                    <h3 class="perm-card__title">{{ tr('GrantAccess', 'Grant access', 'منح صلاحية') }}</h3>
                    <p class="perm-card__subtitle">{{ $t('PermissionLevel') }}</p>
                </div>
            </header>

            <div class="perm-card__body">
                <!-- level selector -->
                <div class="perm-levels" role="radiogroup" :aria-label="$t('PermissionLevel')">
                    <button v-for="level in levels"
                            :key="level.value"
                            type="button"
                            role="radio"
                            class="perm-level"
                            :class="[level.modifier, { 'is-selected': permissionDegreeId === level.value }]"
                            :aria-checked="permissionDegreeId === level.value ? 'true' : 'false'"
                            @click="permissionDegreeId = level.value">
                        <span class="perm-level__icon">
                            <v-icon small>{{ level.icon }}</v-icon>
                        </span>
                        <span class="perm-level__text">{{ level.text }}</span>
                        <v-icon v-if="permissionDegreeId === level.value" class="perm-level__check" small>mdi-check-circle</v-icon>
                    </button>
                </div>

                <!-- target picker -->
                <v-slide-y-transition mode="out-in">
                    <div v-if="permissionDegreeId" key="target" class="perm-target">
                        <div class="perm-target__field">
                            <UserAutocomplete v-if="isUserLevel" v-model="selectedUser" :label="$t('SelectUserSearch')" />
                            <Roleselect v-else-if="isRoleLevel" v-model="selectedRole" :label="$t('SelectRoleSearch')" />
                            <StructureSelect v-else-if="isDepartmentLevel" v-model="selectedDepartment" dense :label="$t('SelectStructureSearch')" />
                            <p v-else class="perm-target__note">
                                <v-icon small class="me-2">mdi-earth</v-icon>
                                {{ tr('EveryoneHint', 'Every authenticated user will be able to access this item.', 'سيتمكن جميع المستخدمين المسجلين من الوصول إلى هذا العنصر.') }}
                            </p>
                        </div>

                        <v-btn v-if="isEveryone"
                               depressed
                               small
                               color="primary"
                               class="perm-btn perm-target__add"
                               @click="addEveryone">
                            <v-icon small class="me-1">mdi-plus</v-icon>
                            {{ $t('Add') }}
                        </v-btn>
                        <v-btn v-else
                               depressed
                               small
                               color="primary"
                               class="perm-btn perm-target__add"
                               :disabled="!hasTarget"
                               @click="addPermission">
                            <v-icon small class="me-1">mdi-plus</v-icon>
                            {{ $t('Add') }}
                        </v-btn>
                    </div>

                    <p v-else key="placeholder" class="perm-target__hint">
                        {{ tr('SelectPermissionLevelHint', 'Choose who the permission applies to.', 'اختر الفئة التي تنطبق عليها الصلاحية.') }}
                    </p>
                </v-slide-y-transition>
            </div>
        </section>

        <!-- ── current permissions ──────────────────────────────────── -->
        <section class="perm-card mt-4">
            <header class="perm-card__head">
                <span class="perm-card__head-icon">
                    <v-icon small>mdi-shield-check-outline</v-icon>
                </span>
                <div class="perm-card__head-text">
                    <h3 class="perm-card__title">{{ $t('Permissions') }}</h3>
                    <p class="perm-card__subtitle">{{ tr('WhoHasAccess', 'Who has access', 'من لديه صلاحية الوصول') }}</p>
                </div>
                <span class="perm-count">{{ permissions.length }}</span>
                <v-spacer />
                <v-text-field v-if="permissions.length"
                              v-model="permissionSearch"
                              class="perm-search"
                              dense
                              outlined
                              rounded
                              hide-details
                              clearable
                              prepend-inner-icon="mdi-magnify"
                              clear-icon="mdi-close-circle-outline"
                              :label="$t('Search')" />
            </header>

            <!-- summary of granted access per level -->
            <div v-if="permissions.length" class="perm-legend">
                <span v-for="level in levels"
                      v-show="levelCounts[level.levelName] > 0"
                      :key="level.value"
                      class="perm-tag"
                      :class="level.modifier">
                    <v-icon x-small class="me-1">{{ level.icon }}</v-icon>
                    {{ level.text }}
                    <strong class="ms-1">{{ levelCounts[level.levelName] }}</strong>
                </span>
                <span class="perm-legend__note">
                    <v-icon x-small class="me-1">mdi-lock-outline</v-icon>
                    {{ tr('DeniedByDefaultHint', 'Anyone not listed below is denied access.', 'كل من لا يظهر في القائمة أدناه ليس لديه صلاحية الوصول.') }}
                </span>
            </div>

            <v-data-table v-if="permissions.length"
                          class="perm-table"
                          dense
                          :headers="headers"
                          :items="permissions"
                          :search="permissionSearch"
                          :items-per-page="5"
                          :loading="loading"
                          :no-results-text="$t('NoItemsToShow')"
                          :no-data-text="$t('NoItemsToShow')">
                <template v-slot:[`item.levelName`]="{ item }">
                    <span class="perm-tag" :class="levelModifier(item.levelName)">
                        <v-icon x-small class="me-1">{{ levelIcon(item.levelName) }}</v-icon>
                        {{ $t(item.levelName) }}
                    </span>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="perm-principal">
                        <span class="perm-principal__avatar" :class="levelModifier(item.levelName)">
                            <v-icon x-small>{{ levelIcon(item.levelName) }}</v-icon>
                        </span>
                        <span class="perm-principal__name">{{ item.name }}</span>
                    </div>
                </template>
                <template v-slot:[`item.access`]>
                    <span class="perm-state perm-state--granted">
                        <v-icon x-small class="me-1">mdi-check-circle</v-icon>
                        {{ tr('Granted', 'Granted', 'ممنوحة') }}
                    </span>
                </template>
                <template v-slot:[`item.options`]="{ item }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon
                                   x-small
                                   class="perm-icon-btn perm-icon-btn--danger"
                                   v-bind="attrs"
                                   :aria-label="$t('Delete')"
                                   v-on="on"
                                   @click="remove(item.id)">
                                <v-icon small>mdi-trash-can-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('Delete') }}</span>
                    </v-tooltip>
                </template>
            </v-data-table>

            <!-- nothing granted yet -->
            <div v-else class="perm-empty">
                <span class="perm-empty__icon">
                    <v-icon large>mdi-lock-outline</v-icon>
                </span>
                <p class="perm-empty__title">{{ tr('NoPermissions', 'No permissions granted', 'لا توجد صلاحيات ممنوحة') }}</p>
                <p class="perm-empty__hint">
                    {{ tr('NoPermissionsHint', 'Access is denied for everyone. Grant access above to make this item visible.', 'الوصول مرفوض للجميع. امنح صلاحية من الأعلى لجعل هذا العنصر مرئياً.') }}
                </p>
            </div>
        </section>

        <!-- ── actions ──────────────────────────────────────────────── -->
        <div class="perm-actions">
            <v-btn depressed
                   small
                   class="perm-btn perm-btn--muted"
                   :disabled="loading"
                   @click="resetPermissions">
                <v-icon small class="me-1">mdi-restore</v-icon>
                {{ $t('Reset') }}
            </v-btn>
            <v-spacer />
            <v-btn depressed
                   small
                   class="perm-btn perm-btn--muted"
                   @click="$emit('close', false)">
                {{ $t('Cancel') }}
            </v-btn>
            <v-btn depressed
                   small
                   color="secondary"
                   class="perm-btn white--text ms-3"
                   :loading="loading"
                   @click="savePermissions">
                <v-icon small class="me-1">mdi-content-save-outline</v-icon>
                {{ $t('Save') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
    import UserAutocomplete from '@/components/controls/UserSelect'
    import Roleselect from '@/components/controls/RoleSelect'
    import StructureSelect from '@/components/controls/StructureSelect.vue'

    import { PermissionLevelsEnum } from '@/helpers/constants/enumerations.js'
    import LookupsService from '@/services/LookupsService.js'
    import PermissionsService from '@/services/PermissionsService.js'

    export default {
        components: {
            UserAutocomplete, Roleselect, StructureSelect
        },

        props: {
            itemId: {
                type: Number,
            },

            typeId: {
                type: Number,
            },

            // optional label of the item being secured, shown as context in the header
            contextName: {
                type: String,
                default: ''
            },

            contextIcon: {
                type: String,
                default: 'mdi-folder-outline'
            },
        },

        data() {
            return {
                loading: false,
                permissionDegrees: [{ text: this.$t('User'), value: 1 }, { text: this.$t('Role'), value: 2 }, { text: this.$t('Department'), value: 3 }, { text: this.$t('Everyone'), value: 4 }],
                permissionDegreeId: null,
                permissionSearch: '',
                selectedDepartment: null,
                selectedUser: null,
                selectedRole: null,
                permissions: [],
                headers: [
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('Level'), value: 'levelName' },
                    { text: this.$t('Name'), value: 'name' },
                    { text: this.$t('Permissions'), value: 'access', sortable: false, filterable: false },
                    { text: this.$t('Options'), value: 'options', sortable: false, filterable: false, align: 'center' },
                ],
                // presentation only: icon + colour scheme per permission level
                levelStyles: {
                    1: { icon: 'mdi-account-outline', modifier: 'is-user', levelName: 'User' },
                    2: { icon: 'mdi-shield-account-outline', modifier: 'is-role', levelName: 'Role' },
                    3: { icon: 'mdi-sitemap-outline', modifier: 'is-department', levelName: 'Department' },
                    4: { icon: 'mdi-earth', modifier: 'is-everyone', levelName: 'Everyone' },
                },
            }
        },

        computed: {
            isUserLevel() {
                return this.permissionDegreeId == PermissionLevelsEnum.User
            },

            isRoleLevel() {
                return this.permissionDegreeId == PermissionLevelsEnum.Role
            },

            isDepartmentLevel() {
                return this.permissionDegreeId == PermissionLevelsEnum.Department
            },

            isEveryone() {
                return this.permissionDegreeId == PermissionLevelsEnum.Everyone
            },

            // the four selectable levels, decorated with icons for the level picker
            levels() {
                return this.permissionDegrees.map(degree => ({
                    ...degree,
                    ...(this.levelStyles[degree.value] || {})
                }))
            },

            // enables the Add button only once a target has been picked
            hasTarget() {
                if (this.isUserLevel) {
                    return !!this.selectedUser
                }
                if (this.isRoleLevel) {
                    return !!this.selectedRole
                }
                if (this.isDepartmentLevel) {
                    return !!this.selectedDepartment
                }
                return false
            },

            levelCounts() {
                return this.permissions.reduce((counts, permission) => {
                    const key = permission.levelName
                    counts[key] = (counts[key] || 0) + 1
                    return counts
                }, {})
            },
        },

        created() {
            this.loadPermissions()
        },

        methods: {

            // translate with a built-in fallback, so labels still read correctly when
            // the key has not been added to the translations table yet
            tr(key, fallbackEn, fallbackAr) {
                if (this.$te(key)) {
                    return this.$t(key)
                }
                return this.$i18n.locale === 'ar' ? fallbackAr : fallbackEn
            },

            styleFor(levelName) {
                return Object.values(this.levelStyles).find(style => style.levelName === levelName) || {}
            },

            levelIcon(levelName) {
                return this.styleFor(levelName).icon || 'mdi-shield-outline'
            },

            levelModifier(levelName) {
                return this.styleFor(levelName).modifier || ''
            },

            // discards unsaved changes by re-reading the stored permissions
            resetPermissions() {
                this.permissionDegreeId = null
                this.selectedUser = null
                this.selectedRole = null
                this.selectedDepartment = null
                this.permissionSearch = ''
                this.loading = true
                this.loadPermissions()
            },

            async addPermission() {

                switch (this.permissionDegreeId) {
                    case PermissionLevelsEnum.User:
                        if (this.selectedUser) {
                            let user = await this.getUser(this.selectedUser)
                            this.permissions.push({ levelId: PermissionLevelsEnum.User, id: this.selectedUser, name: user, levelName: 'User' })
                            this.selectedUser = null
                            this.selectedRole = null
                            this.permissionDegreeId = null
                        }
                        break
                    case PermissionLevelsEnum.Role:
                        if (this.selectedRole) {
                            let role = await this.getRole(this.selectedRole)
                            this.permissions.push({ levelId: PermissionLevelsEnum.Role, id: this.selectedRole, name: role, levelName: 'Role' })
                            this.selectedUser = null
                            this.selectedRole = null
                            this.permissionDegreeId = null
                        }
                        break
                    case PermissionLevelsEnum.Department:
                        if (this.selectedDepartment) {
                            let department = await this.getDepartment(this.selectedDepartment)
                            this.permissions.push({ levelId: PermissionLevelsEnum.Department, id: this.selectedDepartment, name: department, levelName: 'Department' })
                            this.selectedUser = null
                            this.selectedRole = null
                            this.permissionDegreeId = null
                        }
                        break
                }
            },

            addEveryone() {
                this.permissions.push({ levelId: PermissionLevelsEnum.Everyone, id: 1, name: this.$t('Everyone'), levelName: 'Everyone' })
            },

            getUser(userId) {
                this.loading = true
                return LookupsService.getUserById(userId).then((res) => {
                    if (res.data) {
                        return res.data.name
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.loading = false })
            },

            getRole(roleId) {
                this.loading = true
                return LookupsService.getRoleById(roleId).then((res) => {
                    if (res.data) {
                        return res.data.name
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.loading = false })
            },

            getDepartment(departmentId) {
                this.loading = true
                return LookupsService.getDepartmentById(departmentId).then((res) => {
                    if (res.data) {
                        return res.data.name
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.loading = false })
            },

            remove(itemId) {
                this.permissions = this.permissions.filter(x => x.id != itemId)
            },

            savePermissions() {
                var postData = this.permissions.map(x => ({ itemId: this.itemId, typeId: this.typeId, levelId: x.levelId, value: x.id }))
                if (postData.length == 0) {
                    postData.push({ itemId: this.itemId, typeId: this.typeId, AllDeleted: true })
                }
                this.loading = true
                return PermissionsService.post(postData).then((res) => {
                    if (res.success) {
                        this.$root.$notification(this.$t('SaveSuccess'), 'success')
                        this.$emit('close', false)
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.loading = false })
            },

            loadPermissions() {
                PermissionsService.loadPermissions(this.itemId, this.typeId).then((res) => {
                    if (res.data) {
                        this.permissions = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => { this.loading = false })
            }
        }
    }
</script>

<style>
    /* ==========================================================================
       Permissions manager (folder / item access control)
       Rendered inside a dialog, so every rule is namespaced with .perm*.
       ========================================================================== */

    .perm {
        --perm-surface: #ffffff;
        --perm-border: #e4e9ee;
        --perm-text: #2b3a42;
        --perm-muted: #78899a;
        --perm-radius: 14px;
        /* Muted teal-green accent, matching the e-library and organizational-structure
           pages. It replaces --v-primary-lighten5, which resolved to a cyan. */
        --perm-tint: #84a6a5;
        padding: 18px 20px 16px;
        background: #f7f9fa;
    }

    /* ---------- context strip ---------- */
    .perm__context {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        margin-bottom: 16px;
        border-radius: 12px;
        border: 1px solid var(--perm-border);
        background: var(--perm-surface);
    }

    .perm__context-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        flex: 0 0 34px;
        border-radius: 10px;
        background: var(--perm-tint);
    }

    .perm__context-icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .perm__context-eyebrow {
        font-size: 11px;
        letter-spacing: .05em;
        text-transform: uppercase;
        color: var(--perm-muted);
        line-height: 1.4;
    }

    .perm__context-name {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--perm-text);
        line-height: 1.4;
        word-break: break-word;
    }

    /* ---------- cards ---------- */
    .perm-card {
        background: var(--perm-surface);
        border: 1px solid var(--perm-border);
        border-radius: var(--perm-radius);
        overflow: hidden;
    }

    .perm-card__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--perm-border);
        background: linear-gradient(180deg, #fbfcfd 0%, #f6f8fa 100%);
    }

    .perm-card__head-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        flex: 0 0 30px;
        border-radius: 9px;
        background: var(--perm-tint);
    }

    .perm-card__head-icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .perm-card__head-text {
        min-width: 0;
    }

    .perm-card__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--perm-text);
        line-height: 1.35;
        margin: 0;
    }

    .perm-card__subtitle {
        font-size: 12px;
        color: var(--perm-muted);
        line-height: 1.4;
        margin: 0;
    }

    .perm-card__body {
        padding: 16px;
    }

    .perm-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 26px;
        height: 22px;
        padding: 0 8px;
        border-radius: 999px;
        background: var(--perm-tint);
        color: var(--v-primary-base);
        font-size: 12px;
        font-weight: 600;
    }

    /* ---------- level picker ---------- */
    .perm-levels {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 10px;
    }

    .perm-level {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 8px;
        border: 1px solid var(--perm-border);
        border-radius: 12px;
        background: #fff;
        cursor: pointer;
        transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
        font: inherit;
        color: var(--perm-text);
        text-align: center;
        width: 100%;
    }

    .perm-level:hover {
        border-color: #c6d2da;
        background: #fbfcfd;
    }

    .perm-level:focus-visible,
    .perm-level:focus {
        outline: none;
        box-shadow: 0 0 0 2px var(--v-primary-base);
    }

    .perm-level__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 11px;
        background: #eff3f6;
        transition: background .18s ease;
    }

    .perm-level__icon .v-icon {
        color: var(--perm-muted) !important;
    }

    .perm-level__text {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
    }

    .perm-level__check {
        position: absolute;
        top: 6px;
        color: var(--v-primary-base) !important;
    }

    .v-application--is-ltr .perm-level__check {
        right: 6px;
    }

    .v-application--is-rtl .perm-level__check {
        left: 6px;
    }

    .perm-level.is-selected {
        border-color: var(--v-primary-base);
        background: var(--perm-tint);
        box-shadow: inset 0 0 0 1px var(--v-primary-base);
    }

    /* per-level accent colours, applied to the picker and to list badges */
    .perm-level.is-user.is-selected .perm-level__icon,
    .perm-level.is-user:hover .perm-level__icon {
        background: #e6f0fd;
    }

    .perm-level.is-user.is-selected .perm-level__icon .v-icon {
        color: #1f6feb !important;
    }

    .perm-level.is-role.is-selected .perm-level__icon,
    .perm-level.is-role:hover .perm-level__icon {
        background: #f1ebfb;
    }

    .perm-level.is-role.is-selected .perm-level__icon .v-icon {
        color: #7a4fd0 !important;
    }

    .perm-level.is-department.is-selected .perm-level__icon,
    .perm-level.is-department:hover .perm-level__icon {
        background: var(--perm-tint);
    }

    .perm-level.is-department.is-selected .perm-level__icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .perm-level.is-everyone.is-selected .perm-level__icon,
    .perm-level.is-everyone:hover .perm-level__icon {
        background: #fdf1e0;
    }

    .perm-level.is-everyone.is-selected .perm-level__icon .v-icon {
        color: #b26a00 !important;
    }

    /* ---------- target picker ---------- */
    .perm-target {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px dashed var(--perm-border);
    }

    .perm-target__field {
        flex: 1 1 auto;
        min-width: 0;
    }

    .perm-target__add {
        flex: 0 0 auto;
        margin-bottom: 20px;
    }

    .perm-target__note {
        display: flex;
        align-items: center;
        margin: 0 0 20px;
        padding: 10px 12px;
        border-radius: 10px;
        background: #fdf6ea;
        color: #8a6317;
        font-size: 13px;
        line-height: 1.5;
    }

    .perm-target__note .v-icon {
        color: #b26a00 !important;
    }

    .perm-target__hint {
        margin: 14px 0 0;
        font-size: 13px;
        color: var(--perm-muted);
        text-align: center;
    }

    /* ---------- legend / summary ---------- */
    .perm-legend {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--perm-border);
        background: #fbfcfd;
    }

    .perm-legend__note {
        display: inline-flex;
        align-items: center;
        margin-inline-start: auto;
        font-size: 12px;
        color: var(--perm-muted);
    }

    /* ---------- level tag ---------- */
    .perm-tag {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 999px;
        background: #eff3f6;
        color: var(--perm-text);
        font-size: 12px;
        font-weight: 600;
        line-height: 18px;
        white-space: nowrap;
    }

    .perm-tag .v-icon {
        color: inherit !important;
    }

    .perm-tag.is-user {
        background: #e6f0fd;
        color: #1f6feb;
    }

    .perm-tag.is-role {
        background: #f1ebfb;
        color: #7a4fd0;
    }

    .perm-tag.is-department {
        background: var(--perm-tint);
        color: var(--v-primary-base);
    }

    .perm-tag.is-everyone {
        background: #fdf1e0;
        color: #b26a00;
    }

    /* ---------- table ---------- */
    .perm-search {
        flex: 0 1 220px;
        min-width: 160px;
    }

    .perm-search .v-input__slot {
        background: #fff !important;
    }

    .perm-table thead th {
        background: #fbfcfd;
        white-space: nowrap;
        border-bottom: 1px solid var(--perm-border) !important;
    }

    .perm-table tbody tr {
        transition: background .15s ease;
    }

    .perm-table tbody tr:hover {
        background: var(--perm-tint) !important;
    }

    .perm-table tbody td {
        border-bottom: 1px solid #f0f3f5 !important;
    }

    .perm-principal {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    .perm-principal__avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex: 0 0 24px;
        border-radius: 50%;
        background: #eff3f6;
        color: var(--perm-muted);
    }

    .perm-principal__avatar .v-icon {
        color: inherit !important;
    }

    .perm-principal__avatar.is-user {
        background: #e6f0fd;
        color: #1f6feb;
    }

    .perm-principal__avatar.is-role {
        background: #f1ebfb;
        color: #7a4fd0;
    }

    .perm-principal__avatar.is-department {
        background: var(--perm-tint);
        color: var(--v-primary-base);
    }

    .perm-principal__avatar.is-everyone {
        background: #fdf1e0;
        color: #b26a00;
    }

    .perm-principal__name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .perm-state {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 18px;
        white-space: nowrap;
    }

    .perm-state .v-icon {
        color: inherit !important;
    }

    .perm-state--granted {
        background: #e7f4ee;
        color: #1b8354;
    }

    /* ---------- buttons ---------- */
    .perm-btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-size: 14px !important;
        font-weight: 600;
        padding: 0 14px;
    }

    .perm-btn--muted {
        background: #eef1f4 !important;
        color: var(--perm-text) !important;
    }

    /* icon-only buttons fill with colour on hover/focus, because the global
       stylesheet forces icons inside a hovered/focused button to white */
    .perm-icon-btn {
        border-radius: 9px;
        background: #eff3f6;
        transition: background .18s ease;
    }

    .perm-icon-btn .v-icon {
        color: var(--perm-muted);
    }

    .perm-icon-btn:hover,
    .perm-icon-btn:focus {
        background: var(--v-primary-base);
    }

    .perm-icon-btn--danger:hover,
    .perm-icon-btn--danger:focus {
        background: var(--v-error-base);
    }

    .perm-actions {
        display: flex;
        align-items: center;
        margin-top: 18px;
        padding-top: 16px;
        border-top: 1px solid var(--perm-border);
    }

    /* ---------- empty state ---------- */
    .perm-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 34px 24px;
    }

    .perm-empty__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: #f1f4f6;
        margin-bottom: 14px;
    }

    .perm-empty__icon .v-icon {
        color: var(--perm-muted) !important;
    }

    .perm-empty__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: var(--perm-text);
        margin: 0 0 6px;
    }

    .perm-empty__hint {
        font-size: 13px;
        color: var(--perm-muted);
        max-width: 340px;
        margin: 0;
        line-height: 1.6;
    }

    /* ---------- responsive ---------- */
    @media (max-width: 767px) {
        .perm {
            padding: 14px 12px 12px;
        }

        .perm-levels {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .perm-target {
            flex-direction: column;
            align-items: stretch;
        }

        .perm-target__add {
            margin-bottom: 0;
            width: 100%;
        }

        .perm-search {
            flex: 1 1 100%;
        }

        .perm-legend__note {
            margin-inline-start: 0;
            width: 100%;
        }

        .perm-actions {
            flex-wrap: wrap;
            gap: 8px;
        }
    }
</style>
